// The paywall's contents inside a billing-capable build of the Android app.
//
// Mounted by Paywall INSTEAD of ProPurchasePanel when purchaseSurface() is
// 'play'. It sells nothing itself: it hands off to the native Play paywall
// (PaywallActivity) with a short-lived link token, and Play shows its own
// localized prices and runs the purchase. So, by construction:
//  - It never calls configureRc, getProOffering or purchasePro. The web
//    checkout stays refused inside the app (Play policy), and this module
//    does not even import the RevenueCat wrapper.
//  - It shows no prices. A USD fallback would be wrong for most Play
//    customers; Play shows the real ones one tap later.
//
// The launch has to be SYNCHRONOUS. Chrome only hands an intent: URL to the
// app from a user gesture, and an await between the tap and the navigation
// can lose it on a slow network. So the token is fetched AHEAD (on mount, per
// account and device key), and the button is a plain <a href> built from the
// ready token, like the scanner links (src/hooks/useScannerLaunch.ts).
//
// A token is single use (the server consumes it on resolve), so:
//  - on tap it is marked spent synchronously (a ref, then state): a second
//    tap in the same frame is swallowed, the button turns into "Getting
//    ready..." and a replacement is fetched straight away;
//  - on return from the native screen (visibilitychange to visible) a spent
//    or expiring token is always replaced, so cancel -> reopen works at once;
//  - it is refreshed REFRESH_BEFORE_MS before it expires, and it is never
//    offered past expiry: a response with under MIN_LIFETIME_MS left is
//    rejected, a timer retires it EXPIRY_MARGIN_MS before it dies (so a
//    failed or stalled refresh leaves "Getting ready...", not a dead link),
//    and the tap itself rechecks the clock, because a backgrounded tab's
//    timers can be suspended past the expiry;
//  - a token belongs to one account: Paywall remounts this panel per user
//    id (key={userId}), and the render also checks the owner, so no token
//    minted for one account is ever on screen for another.
//
// Guests (anonymous Supabase sessions) and email-less accounts cannot buy:
// the server answers 403 email_required and the native resolve refuses them
// too. They see "Sign in to subscribe" instead of a launch button.

import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'
import { fetchPlayLinkToken, isEmailRequired } from '../../api/playLink'
import { useSupabaseToken } from '../../hooks/useSupabaseToken'
import { trackEvent } from '../../lib/analytics'
import { buildPaywallIntent, type PaywallPlan } from '../../lib/androidApp'
import { deviceKeyHash, readDeviceKey } from '../../lib/deviceKey'
import AuthModal from '../auth/AuthModal'
import { legalWithLinks } from './legalWithLinks'
import {
  paywallLegalPlay,
  paywallPlayAlreadyPro,
  paywallPlayFailed,
  paywallPlayLaunch,
  paywallPlayPlanGroup,
  paywallPlayPreparing,
  paywallPlayRetry,
  paywallPlaySignin,
  planAnnualLabel,
  planAnnualNote,
  planMonthlyLabel,
} from './proCopy'

/** Fetch a replacement this long before the token's expiry. */
export const REFRESH_BEFORE_MS = 60_000
/** Never refresh more often than this, whatever the clocks say. */
export const MIN_REFRESH_MS = 15_000
/**
 * A token is accepted only with at least this much life left, and stops
 * being launchable this long before its expiry: the native screen still has
 * to resolve it, and a token that dies on the way is a failed purchase start.
 */
export const MIN_LIFETIME_MS = MIN_REFRESH_MS
export const EXPIRY_MARGIN_MS = 10_000

/** Still launchable at `now` (ms): a finite expiry more than the margin away. */
export function linkLive(expiresAtSec: number, now: number = Date.now()): boolean {
  return Number.isFinite(expiresAtSec) && expiresAtSec * 1000 - now > EXPIRY_MARGIN_MS
}

export interface AndroidPlayPurchasePanelProps {
  /** Supabase user id; null when signed out. */
  userId: string | null
  /** Supabase access token, the bearer for /pro/play/link-token. */
  token: string | null
  /** Already entitled: nothing to sell. */
  isPro?: boolean
  /** Which entry point opened the sheet, passed on to the native paywall. */
  entry: string
}

interface ReadyLink {
  owner: string
  token: string
  /** Unix seconds. */
  expiresAt: number
  href: string
}

type Problem = 'email_required' | 'failed'

export default function AndroidPlayPurchasePanel({ userId, token, isPro = false, entry }: AndroidPlayPurchasePanelProps) {
  const { anonymous } = useSupabaseToken()
  const deviceKey = readDeviceKey()
  const [plan, setPlan] = useState<PaywallPlan | null>(null)
  const [authOpen, setAuthOpen] = useState(false)

  // The bearer is read at fetch time, not a dependency: a token refresh
  // (hourly) must not re-mint a link token for nothing.
  const bearerRef = useRef(token)
  useEffect(() => {
    bearerRef.current = token
  }, [token])

  const signedIn = !!userId && !!token
  const canFetch = signedIn && !anonymous && !isPro && !!deviceKey

  // Bumped to ask for a fresh token: after a tap, on refresh, on retry, on
  // return from the native screen.
  const [request, setRequest] = useState(0)
  const [link, setLink] = useState<ReadyLink | null>(null)
  const [problem, setProblem] = useState<{ owner: string; kind: Problem } | null>(null)
  const [spent, setSpent] = useState<string | null>(null)
  // The token the expiry timer (or a late tap) retired.
  const [expired, setExpired] = useState<string | null>(null)
  const spentRef = useRef<string | null>(null)
  const linkRef = useRef<ReadyLink | null>(null)
  useEffect(() => {
    linkRef.current = link
  }, [link])

  const owner = canFetch ? userId : null
  useEffect(() => {
    if (!owner || !deviceKey) return
    const controller = new AbortController()
    void (async () => {
      try {
        const hash = await deviceKeyHash(deviceKey)
        const bearer = bearerRef.current
        if (controller.signal.aborted || !bearer) return
        const minted = await fetchPlayLinkToken(bearer, hash, controller.signal)
        if (controller.signal.aborted) return
        const href = buildPaywallIntent({ token: String(minted?.token ?? ''), plan: null, entry })
        // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
        const expiresAt = minted.expires_at
        const lifetimeOk = typeof expiresAt === 'number' && Number.isFinite(expiresAt) && expiresAt * 1000 - Date.now() > MIN_LIFETIME_MS
        // eslint-disable-next-line i18next/no-literal-string -- developer message, never shown
        if (!href || !lifetimeOk) throw new Error('malformed or expired link token')
        setLink({ owner, token: minted.token, expiresAt: minted.expires_at, href })
        setProblem(null)
      } catch (err) {
        if (controller.signal.aborted) return
        // eslint-disable-next-line i18next/no-literal-string -- problem codes
        setProblem({ owner, kind: isEmailRequired(err) ? 'email_required' : 'failed' })
      }
    })()
    return () => controller.abort()
  }, [owner, deviceKey, request, entry])

  // Refresh ahead of expiry.
  const readyToken = link?.token ?? null
  const readyExpiry = link?.expiresAt ?? null
  useEffect(() => {
    if (readyToken === null || readyExpiry === null) return
    const delay = Math.max(MIN_REFRESH_MS, readyExpiry * 1000 - Date.now() - REFRESH_BEFORE_MS)
    const timer = setTimeout(() => setRequest(n => n + 1), delay)
    return () => clearTimeout(timer)
  }, [readyToken, readyExpiry])

  // Retire the token just before it dies, whether or not its refresh landed.
  useEffect(() => {
    if (readyToken === null || readyExpiry === null) return
    const timer = setTimeout(() => setExpired(readyToken), Math.max(0, readyExpiry * 1000 - Date.now() - EXPIRY_MARGIN_MS))
    return () => clearTimeout(timer)
  }, [readyToken, readyExpiry])

  // Back from the native paywall (or any return to this tab): a spent or
  // nearly expired token is always replaced before it can be offered again.
  useEffect(() => {
    if (!owner) return
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const current = linkRef.current
      const stale =
        !current ||
        current.token === spentRef.current ||
        current.expiresAt * 1000 - Date.now() <= REFRESH_BEFORE_MS
      if (stale) setRequest(n => n + 1)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [owner])

  const usable = owner && link && link.owner === owner && link.token !== spent && link.token !== expired ? link : null
  const href = usable ? buildPaywallIntent({ token: usable.token, plan, entry }) : null

  const onLaunch = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      // Spent in the same frame (a double tap): never follow it twice.
      if (!usable || spentRef.current === usable.token) {
        e.preventDefault()
        return
      }
      // Timers can be suspended in a background tab: the clock, not the
      // expiry timer, decides whether this token can still be resolved.
      if (!linkLive(usable.expiresAt)) {
        e.preventDefault()
        setExpired(usable.token)
        setRequest(n => n + 1)
        return
      }
      spentRef.current = usable.token
      setSpent(usable.token)
      setRequest(n => n + 1)
      // eslint-disable-next-line i18next/no-literal-string -- analytics props
      trackEvent('pro_play_paywall_launched', { plan: plan ?? 'none', entry })
      // No preventDefault: the href IS the launch.
    },
    [usable, plan, entry],
  )

  if (isPro) {
    return (
      <div className="space-y-2" data-testid="pro-play-panel">
        <p data-testid="pro-play-already-pro" className="text-sm text-gray-300">{paywallPlayAlreadyPro()}</p>
      </div>
    )
  }

  const ownProblem = problem && problem.owner === owner ? problem.kind : null
  const needsSignIn = !signedIn || !!anonymous || (ownProblem === 'email_required' && !usable)

  return (
    <div className="space-y-3" data-testid="pro-play-panel">
      {needsSignIn ? (
        <>
          <button
            type="button"
            onClick={() => setAuthOpen(true)}
            data-testid="pro-play-signin"
            className="w-full px-4 py-3 rounded-xl border border-purple-500/50 bg-purple-600/15 text-sm font-medium text-purple-100 hover:bg-purple-600/25 transition-colors"
          >
            {paywallPlaySignin()}
          </button>
          <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
        </>
      ) : (
        <>
          <div role="group" aria-label={paywallPlayPlanGroup()} className="flex gap-2">
            <PlanChip
              // eslint-disable-next-line i18next/no-literal-string -- plan code
              value="monthly"
              label={planMonthlyLabel()}
              selected={plan}
              onSelect={setPlan}
            />
            <PlanChip
              // eslint-disable-next-line i18next/no-literal-string -- plan code
              value="annual"
              label={planAnnualLabel()}
              note={planAnnualNote()}
              selected={plan}
              onSelect={setPlan}
            />
          </div>
          {href ? (
            <a
              href={href}
              onClick={onLaunch}
              data-testid="pro-play-launch"
              className="block w-full text-center px-4 py-3 rounded-xl border border-purple-500/50 bg-purple-600/25 text-sm font-semibold text-purple-50 hover:bg-purple-600/35 transition-colors"
            >
              {paywallPlayLaunch()}
            </a>
          ) : ownProblem === 'failed' ? (
            <div className="space-y-2">
              <p data-testid="pro-play-failed" className="text-xs text-gray-400">{paywallPlayFailed()}</p>
              <button
                type="button"
                onClick={() => setRequest(n => n + 1)}
                data-testid="pro-play-retry"
                className="w-full px-4 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
              >
                {paywallPlayRetry()}
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled
              data-testid="pro-play-preparing"
              className="w-full px-4 py-3 rounded-xl border border-gray-700 bg-gray-900/60 text-sm text-gray-400 cursor-wait"
            >
              {paywallPlayPreparing()}
            </button>
          )}
        </>
      )}

      <p className="pt-1 text-[11px] leading-snug text-gray-500" data-testid="pro-purchase-legal">
        {legalWithLinks(paywallLegalPlay())}
      </p>
    </div>
  )
}

function PlanChip({
  value,
  label,
  note,
  selected,
  onSelect,
}: {
  value: PaywallPlan
  label: string
  note?: string
  selected: PaywallPlan | null
  onSelect: (plan: PaywallPlan | null) => void
}) {
  const on = selected === value
  return (
    <button
      type="button"
      aria-pressed={on}
      data-testid={`pro-play-plan-${value}`}
      onClick={() => onSelect(on ? null : value)}
      className={`flex-1 px-3 py-2 rounded-xl border text-sm transition-colors ${
        on ? 'border-purple-400 bg-purple-600/30 text-purple-50' : 'border-gray-700 bg-gray-900/60 text-gray-300 hover:bg-gray-800'
      }`}
    >
      {label}
      {note && (
        <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-600/25 border border-purple-500/40 text-purple-200">
          {note}
        </span>
      )}
    </button>
  )
}
